import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PageHeader from '../../components/PageHeader';
import { Icon } from '../../components/icons';
import { formatDate, formatMoney } from '../../utils/format';

interface Spending {
  id: number;
  date: string;
  amount: number;
  description: string;
  studentName: string;
}

const SpendingsList: React.FC<{ startDate: string | null, endDate: string | null, schoolYear: string }> = ({ startDate, endDate, schoolYear }) => {
  const [spendings, setSpendings] = useState<Spending[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedDescription, setEditedDescription] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    const fetchSpendings = async () => {
      try {
        const response = await axios.get<Spending[]>('/api/payments/spendings', { params: { startDate, endDate, schoolYear } });
        if (!cancelled) setSpendings(response.data);
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (startDate && endDate && schoolYear) {
      fetchSpendings();
    }
    return () => { cancelled = true; };
  }, [startDate, endDate, schoolYear]);

  const handleDescriptionClick = (id: number, currentDescription: string) => {
    setEditingId(id);
    setEditedDescription(currentDescription);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditedDescription(e.target.value);
  };

  const handleDescriptionSave = async (id: number) => {
    try {
      await axios.put('/api/payments/spendings/description', {
        id: id,
        description: editedDescription,
      });
      // Update the local state with the new description
      setSpendings((prevSpendings) =>
        prevSpendings.map((spending) =>
          spending.id === id ? { ...spending, description: editedDescription } : spending
        )
      );
      setEditingId(null); // Exit editing mode
    } catch (e: any) {
      console.error('Failed to update description:', e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, id: number) => {
    if (e.key === 'Enter') {
      handleDescriptionSave(id);
    } else if (e.key === 'Escape') {
      setEditingId(null); // Exit editing mode without saving
    }
  };

  const totalSpendings = spendings.reduce((sum, spending) => sum + spending.amount, 0);

  const subtitle = loading || error
    ? `Rok szkolny ${schoolYear}`
    : spendings.length === 0
      ? `Rok szkolny ${schoolYear}`
      : `Rok szkolny ${schoolYear} · ${spendings.length} ${spendings.length === 1 ? 'pozycja' : 'pozycji'} · łącznie ${formatMoney(totalSpendings, { fixed: true })}`;

  return (
    <>
      <PageHeader title="Wydatki" subtitle={subtitle} />
      {loading && <div className="loading-state"><span className="spinner-border spinner-border-sm" role="status" />Ładowanie wydatków…</div>}
      {error && <div className="alert alert-danger" role="alert">Nie udało się pobrać wydatków: {error}</div>}
      {!loading && !error && spendings.length === 0 && (
        <div className="surface empty-state">Brak zarejestrowanych wydatków.</div>
      )}
      {!loading && !error && spendings.length > 0 && (
        <div className="surface table-card">
          <div className="table-scroll">
            <table className="table data-table table-stack">
              <thead>
                <tr>
                  <th scope="col">Data</th>
                  <th className="num" scope="col">Kwota</th>
                  <th scope="col">Odbiorca</th>
                  <th scope="col">Opis <span className="text-muted fw-normal text-lowercase">(kliknij, aby edytować)</span></th>
                </tr>
              </thead>
              <tbody>
                {spendings.map((spending) => (
                  <tr key={spending.id}>
                    <td data-label="Data" className="nowrap">{formatDate(spending.date)}</td>
                    <td data-label="Kwota" className="num">{formatMoney(spending.amount, { fixed: true })}</td>
                    <td data-label="Odbiorca" className={spending.studentName ? undefined : 'muted'}>{spending.studentName || '—'}</td>
                    <td data-label="Opis">
                      {editingId === spending.id ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          aria-label="Opis wydatku"
                          value={editedDescription}
                          onChange={handleDescriptionChange}
                          onBlur={() => handleDescriptionSave(spending.id)}
                          onKeyDown={(e) => handleKeyDown(e, spending.id)}
                          autoFocus
                        />
                      ) : (
                        <button
                          type="button"
                          className="inline-edit"
                          onClick={() => handleDescriptionClick(spending.id, spending.description)}
                          title="Edytuj opis"
                        >
                          {spending.description || <span className="text-muted">Dodaj opis</span>}
                          <Icon name="pencil" size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Suma</td>
                  <td className="num">{formatMoney(totalSpendings, { fixed: true })}</td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default SpendingsList;
